import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Home, Layers, Tag, Briefcase, User, BookOpen, UserPlus, Mail, Calendar, Shield, X } from "lucide-react";

const ITEMS = [
  { label: "Home", to: "/", icon: Home, hint: "Landing" },
  { label: "Services", to: "/services", icon: Layers, hint: "What we build" },
  { label: "Pricing", to: "/pricing", icon: Tag, hint: "Package & costs" },
  { label: "Case Studies", to: "/case-studies", icon: Briefcase, hint: "Case studies" },
  { label: "About", to: "/about", icon: User, hint: "Founder & story" },
  { label: "Blog / Insights", to: "/blog", icon: BookOpen, hint: "Articles" },
  { label: "Careers", to: "/careers", icon: UserPlus, hint: "Join us" },
  { label: "Book Free Consultation", to: "/book", icon: Calendar, hint: "Schedule" },
  { label: "Contact", to: "/contact", icon: Mail, hint: "Reach out" },
  { label: "FAQ", to: "/faq", icon: Search, hint: "Common questions" },
  { label: "Admin Login", to: "/admin", icon: Shield, hint: "Restricted" },
];

export default function CommandPalette({ open, setOpen }: { open: boolean; setOpen: (b: boolean) => void }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(!open);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, setOpen]);

  const filtered = ITEMS.filter(i =>
    !q || i.label.toLowerCase().includes(q.toLowerCase()) || i.hint.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="command-palette"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.95, y: -10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: -10 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg glass glass-edge overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
              <Search size={16} className="text-edx-cyan" />
              <input
                autoFocus
                data-testid="palette-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search pages, actions, or ask NOVA…"
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-edx-muted"
              />
              <kbd className="font-mono text-[10px] text-edx-muted border border-white/10 rounded px-1.5 py-0.5">ESC</kbd>
              <button onClick={() => setOpen(false)}><X size={14} className="text-edx-muted" /></button>
            </div>
            <div className="max-h-[50vh] overflow-y-auto p-2">
              {filtered.length === 0 ? (
                <div className="px-4 py-8 text-center text-edx-muted text-sm">No matches. Try a different query.</div>
              ) : (
                filtered.map((i) => (
                  <button
                    key={i.to}
                    data-testid={`palette-item-${i.to}`}
                    onClick={() => { nav(i.to); setOpen(false); setQ(""); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-edx-cyan/10 text-left transition-colors group"
                  >
                    <div className="h-8 w-8 rounded-lg bg-white/5 group-hover:bg-edx-cyan/15 text-edx-cyan flex items-center justify-center">
                      <i.icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-medium">{i.label}</div>
                      <div className="text-[11px] text-edx-muted">{i.hint}</div>
                    </div>
                    <span className="text-[10px] font-mono text-edx-muted">↵</span>
                  </button>
                ))
              )}
            </div>
            <div className="px-4 py-2 border-t border-white/5 text-[10px] font-mono text-edx-muted">
              Press <kbd className="border border-white/10 rounded px-1">⌘</kbd> + <kbd className="border border-white/10 rounded px-1">K</kbd> to open · powered by NOVA
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
